import React, { useRef } from "react";

import {
	Button,
	Checkbox,
	Input,
	NumberInput,
	SectionCard,
	TextArea,
} from "@amphore-dev/amphore-lib";
import { FieldArray, useFormikContext } from "formik";
import i18n, { t } from "i18next";

import { EMPTY_INVOICE_LINE, INVOICE_UNIT_KEYS } from "@/constants/CInvoices";

import { TInvoiceFormValues, TInvoiceLine } from "@/types";

import { computeInvoiceTotals, formatMoney } from "@/utils";

import { useInvoiceForm } from "./InvoiceFormContext";

/**
 * The lines, one editable row each: label and details, quantity, unit,
 * unit price, VAT (checkbox + rate) and the computed amount. Rows can be
 * reordered and removed; a new one takes the issuer's VAT defaults.
 */
export const InvoiceLinesEditor: React.FC<{ className?: string }> = ({
	className,
}) => {
	const { values, setFieldValue, errors, touched } =
		useFormikContext<TInvoiceFormValues>();
	const { isLocked, isBusy, billing } = useInvoiceForm();
	const disabled = isLocked || isBusy;
	const language = i18n.language;

	const { lines: computed } = computeInvoiceTotals(values.lines);

	const newLine = (): TInvoiceLine => ({
		...EMPTY_INVOICE_LINE,
		unit: t(`invoices.units.${INVOICE_UNIT_KEYS[0]}`),
		vatApplicable: billing?.vatApplicable ?? false,
		vatRate: billing?.defaultVatRate ?? null,
	});

	// One setFieldValue per key, by path: spreading `values.lines[index]`
	// here would write a stale copy of the line over an edit made in the
	// same tick by another field of the row.
	const setLine = (index: number, patch: Partial<TInvoiceLine>) => {
		for (const [key, value] of Object.entries(patch)) {
			setFieldValue(`lines.${index}.${key}`, value);
		}
	};

	// Lines carry no id (none is sent to the API), so each row gets a key
	// of its own here, moved along with it: keyed by index, a removed or
	// moved row left its neighbour with its state (a resized description).
	// Re-aligned on the count when the whole list is replaced (a prefill).
	const rowKeys = useRef<string[]>([]);
	while (rowKeys.current.length < values.lines.length)
		rowKeys.current.push(crypto.randomUUID());
	rowKeys.current.length = values.lines.length;
	const moveKey = (from: number, to: number) => {
		const keys = rowKeys.current;
		[keys[from], keys[to]] = [keys[to], keys[from]];
	};

	const linesError =
		touched.lines && typeof errors.lines === "string"
			? errors.lines
			: undefined;

	return (
		<FieldArray name="lines">
			{({ push, remove, swap }) => (
				<SectionCard
					title={t("invoices.lines.title")}
					className={className}
					actions={
						!isLocked && (
							<Button
								size="sm"
								variant="outline"
								picto="add"
								onClick={() => {
									rowKeys.current.push(crypto.randomUUID());
									push(newLine());
								}}
								disabled={isBusy}
							>
								{t("invoices.actions.addLine")}
							</Button>
						)
					}
				>
					{values.lines.length === 0 ? (
						<p className="text-sm text-neutral-500">
							{t("invoices.lines.empty")}
						</p>
					) : (
						<div
							className="flex flex-col gap-3"
							data-testid="invoice-lines"
						>
							<div className="hidden gap-2 px-1 text-xs tracking-wide text-neutral-500 uppercase md:grid md:grid-cols-[minmax(0,1fr)_88px_104px_120px_140px_120px_88px]">
								<span>{t("invoices.lines.label")}</span>
								<span className="text-right">
									{t("invoices.lines.quantity")}
								</span>
								<span>{t("invoices.lines.unit")}</span>
								<span className="text-right">
									{t("invoices.lines.unitPrice")}
								</span>
								<span>{t("invoices.lines.vat")}</span>
								<span className="text-right">
									{t("invoices.lines.amount")}
								</span>
								<span />
							</div>
							{values.lines.map((line, index) => (
								<div
									key={rowKeys.current[index]}
									className="grid grid-cols-1 gap-2 rounded-lg border border-neutral-200 p-2 md:grid-cols-[minmax(0,1fr)_88px_104px_120px_140px_120px_88px] md:items-start md:border-0 md:p-0 md:px-1"
									data-testid="invoice-line"
								>
									<div className="flex flex-col gap-1">
										<Input
											aria-label={t(
												"invoices.lines.label"
											)}
											placeholder={t(
												"invoices.lines.label"
											)}
											value={line.label}
											onChange={(value) =>
												setLine(index, { label: value })
											}
											disabled={disabled}
											size="sm"
											hideError
										/>
										<TextArea
											aria-label={t(
												"invoices.lines.description"
											)}
											placeholder={t(
												"invoices.lines.description"
											)}
											value={line.description ?? ""}
											onChange={(value) =>
												setLine(index, {
													description: value,
												})
											}
											disabled={disabled}
											rows={1}
											size="sm"
											hideError
										/>
									</div>
									<NumberInput
										aria-label={t(
											"invoices.lines.quantity"
										)}
										value={line.quantity}
										onChange={(value) =>
											setLine(index, {
												quantity: value ?? 0,
											})
										}
										step={0.5}
										locale={language}
										disabled={disabled}
										size="sm"
										hideError
									/>
									<Input
										aria-label={t("invoices.lines.unit")}
										placeholder={t("invoices.lines.unit")}
										value={line.unit}
										onChange={(value) =>
											setLine(index, { unit: value })
										}
										list="invoice-units"
										disabled={disabled}
										size="sm"
										hideError
									/>
									<NumberInput
										aria-label={t(
											"invoices.lines.unitPrice"
										)}
										value={line.unitPrice}
										onChange={(value) =>
											setLine(index, {
												unitPrice: value ?? 0,
											})
										}
										decimals={2}
										step={50}
										locale={language}
										disabled={disabled}
										size="sm"
										hideError
									/>
									<div className="flex items-center gap-2">
										<Checkbox
											aria-label={t("invoices.lines.vat")}
											checked={line.vatApplicable}
											onChange={(checked) =>
												setLine(index, {
													vatApplicable: checked,
													vatRate:
														checked &&
														line.vatRate == null
															? (billing?.defaultVatRate ??
																null)
															: line.vatRate,
												})
											}
											disabled={disabled}
											hideError
										/>
										<NumberInput
											aria-label={t(
												"invoices.lines.vatRate"
											)}
											value={line.vatRate ?? null}
											onChange={(value) =>
												setLine(index, {
													vatRate: value,
												})
											}
											min={0}
											max={100}
											after="%"
											locale={language}
											disabled={
												disabled || !line.vatApplicable
											}
											size="sm"
											hideError
										/>
									</div>
									<div className="text-right text-sm font-medium tabular-nums md:pt-2">
										{formatMoney(
											computed[index]?.amountHt ?? 0,
											values.currency,
											language
										)}
									</div>
									{!isLocked && (
										<div className="flex justify-end gap-1">
											<Button
												size="sm"
												variant="ghost"
												picto={{
													icon: "chevron",
													rotation: 180,
												}}
												aria-label={t(
													"invoices.actions.moveUp"
												)}
												onClick={() => {
													moveKey(index, index - 1);
													swap(index, index - 1);
												}}
												disabled={isBusy || index === 0}
											/>
											<Button
												size="sm"
												variant="ghost"
												picto="chevron"
												aria-label={t(
													"invoices.actions.moveDown"
												)}
												onClick={() => {
													moveKey(index, index + 1);
													swap(index, index + 1);
												}}
												disabled={
													isBusy ||
													index ===
														values.lines.length - 1
												}
											/>
											<Button
												size="sm"
												variant="ghost"
												color="danger"
												picto="trash"
												aria-label={t(
													"invoices.actions.removeLine"
												)}
												onClick={() => {
													rowKeys.current.splice(
														index,
														1
													);
													remove(index);
												}}
												disabled={isBusy}
											/>
										</div>
									)}
								</div>
							))}
						</div>
					)}
					<datalist id="invoice-units">
						{INVOICE_UNIT_KEYS.map((key) => (
							<option
								key={key}
								value={t(`invoices.units.${key}`)}
							/>
						))}
					</datalist>
					{linesError && (
						<p className="mt-2 text-sm text-red-600">
							{linesError}
						</p>
					)}
					{!isLocked && (
						<p className="mt-3 text-xs text-neutral-500">
							{t("invoices.lines.discountHint")}
						</p>
					)}
				</SectionCard>
			)}
		</FieldArray>
	);
};
